<template>
  <AppIntersectionObserver class="streaming-video"
      :options="{
      rootMargin: '0px',
      threshold: 0.5,
      trackVisibility: true,    // needed for opacity: 0 in homepage hero
      delay: 100                // needed for opacity: 0 in homepage hero
    }"
      @intersect="videoVisible"
  >
    <div class="streaming-video__inner" :class="`streaming-video__inner--${contentFit}`">
      <video
          ref="videoEl"
          disablepictureinpicture
          disableremoteplayback
          preload="metadata"
          playsinline="true"
          webkit-playsinline="true"
          :loop="loop"
          :autoplay="props.autoPlay"
          :muted="computedMuted"
          @loadedmetadata="onVideoInitialized"
          @click="onVideoClick"
          @ended="onEnded"
          @canplay="hideSpinner"
      />
      <div v-if="showSpinner">
        // TODO: @Annemieke we're missing a spinner / loader animation.
        spinner
      </div>
      <button v-if="isPaused && !showSpinner">
        // TODO: @Annemieke not sure if we need this logic
        play btn
      </button>
    </div>
  </AppIntersectionObserver>
</template>

<script setup lang="ts">

import {computed, onMounted, ref, watch} from 'vue'
import type HlsType from 'hls.js'
import {isMobile} from '~/utils/user-agents'
import {useInteractionStore} from '~/store/interaction'
import {useVideoStore} from "~/store/video";
import {useShortsStore} from "~/store/shorts";

const interactedStore = useInteractionStore()
const videoStore = useVideoStore()
const shortsStore = useShortsStore()

const interacted = ref(interactedStore.getInteracted)
const videoEl = ref()
const hlsInstance = ref()
const videoDuration = ref()
const manifestLoaded = ref()
const videoProgress = ref(0)
const isPaused = ref(true)
const hasPlayed = ref(false)
const visible = ref(false)
const showSpinner = ref(true)

let progressInterval: any = null

const props = defineProps({
  hls: {type: String, required: true},
  mp4: {type: String, required: true},
  loop: Boolean,
  interactive: Boolean,
  autoPlay: Boolean,
  muted: Boolean,
  contentFit: String,
  managed: Boolean
})

const emit = defineEmits(['videoStarted', 'videoEnded'])

// setup HLS instance
onMounted(() => {
  initVideo()
})

// watch for user interaction to enable / disable sound
watch(() => interactedStore.getInteracted, () => {
  interacted.value = true
})

const computedMuted = computed(() => {
  return !(interacted.value && !props.muted)
})

const hideSpinner = () => {
  showSpinner.value = false
}

const pause = () => {
  if (!isPaused.value) {
    isPaused.value = true
    if (manifestLoaded.value && videoEl.value) {
      try {
        videoEl.value.pause()
      } catch (e) {
        // silence
      }
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    }
  }
}

const startLoading = () => {
  if (manifestLoaded.value) {
    hlsInstance.value.startLoad(-1)
  }
}

// only trigger play if the user interacted with the viewport
const play = () => {
  if (isPaused.value) {
    startLoading()
    videoEl.value.play()
    emit("videoStarted")
    hasPlayed.value = true
    isPaused.value = false

    progressInterval = setInterval(() => {
      if (!videoEl.value) {
        clearInterval(progressInterval)
        videoProgress.value = 0
      } else {
        videoProgress.value = videoEl.value.currentTime
      }
    }, 125)
  } else {
    isPaused.value = false
  }
}

const destroy = () => {

  pause()
  visible.value = false

  if (props.managed) {
    videoStore.removeVideoFromQueue(videoEl.value)
  }

  if (videoEl.value) {
    videoEl.value.src = null
  }

  if (hlsInstance.value) {
    hlsInstance.value.destroy()
  }

  hasPlayed.value = false
}

const onVideoInitialized = () => {
  videoDuration.value = videoEl.value.duration
}

const initVideo = async () => {
  // Safari and iOS play HLS natively, so hls.js is only downloaded when it is actually needed
  const nativeHls = !!videoEl.value?.canPlayType('application/vnd.apple.mpegurl')
  const Hls: typeof HlsType | undefined = nativeHls ? undefined : (await import('hls.js')).default

  // the component may have been destroyed while the chunk was loading
  if (!videoEl.value) {
    return
  }

  // attempt HLS streaming video (works on most modern devices)
  if (Hls?.isSupported()) {
    hlsInstance.value = new Hls({autoStartLoad: false})
    hlsInstance.value.attachMedia(videoEl.value)
    hlsInstance.value.on(Hls.Events.MANIFEST_PARSED, function (event, data) {
      manifestLoaded.value = true
      if (!isPaused.value && !props.managed) {
        startLoading()
        if (props.autoPlay) {
          play()
        }
      }
    })

    hlsInstance.value.loadSource(props.hls)
  } else if (nativeHls || isMobile.iOS()) {
    // iOS does support HLS native, not via the packages
    videoEl.value.src = props.hls
  } else {
    // else use the provided mp4 fallback
    videoEl.value.src = props.mp4
  }
}

watch(() => videoStore.getActive, (newValue) => {
  if (newValue === videoEl.value) {
    if (visible.value === true) {
      initVideo()
      if (props.autoPlay) {
        play()
      }
    } else {
      pause()
    }
  } else {
    visible.value = false
    pause()
  }
})

watch(() => shortsStore.getCurrentShort, (newValue) => {
  if (props.managed) {
    if (newValue) {
      if (!isPaused.value && visible.value) {
        pause()
      }
    } else {
      if (visible.value && videoStore.getActive === videoEl.value) {
        play()
      }
    }
  }
})

const videoVisible = (data) => {
  if (data.visible && !visible.value && !props.managed) {
    visible.value = true
    initVideo()
    if (props.autoPlay) {
      play()
    }
  } else if (props.managed) {
    // managed videos (global manager that ensures only 1 video plays)
    if (data.visible) {
      visible.value = true
      videoStore.addVideoToQueue(videoEl.value)
    } else {
      visible.value = false
      videoStore.removeVideoFromQueue(videoEl.value)
    }
  } else {
    visible.value = false
    pause()
  }
}

const onVideoClick = () => {
  if (isPaused.value && props.interactive) {
    pause()
  } else {
    play()
  }
}

const onEnded = () => {
  if (!props.loop) {
    emit("videoEnded")
    isPaused.value = true
  }
}

onBeforeUnmount(() => {
  destroy()
})

</script>

<style scoped lang="postcss">
.streaming-video {
  &__inner {
    &--cover {
      height: 100%;
      width: 100%;

      video {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }
  }
}
</style>
